import {
  IconAddressBook,
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowLeft,
  IconBell,
  IconBriefcase,
  IconBuilding,
  IconBuildingBank,
  IconBulb,
  IconCalendar,
  IconCalendarEvent,
  IconCertificate,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconCircleCheck,
  IconCircleCheckFilled,
  IconCircleX,
  IconClock,
  IconCreditCard,
  IconCurrencyDollar,
  IconDashboard,
  IconDatabase,
  IconDots,
  IconDotsVertical,
  IconEye,
  IconFileText,
  IconHash,
  IconHeadset,
  IconKey,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconLink,
  IconList,
  IconLock,
  IconLogin,
  IconLogout,
  IconMail,
  IconMapPin,
  IconMenu2,
  IconMessage,
  IconPhone,
  IconPhoto,
  IconPlus,
  IconReceipt,
  IconRefresh,
  IconRocket,
  IconSearch,
  IconServer,
  IconSettings,
  IconShield,
  IconShieldCancel,
  IconShieldCheck,
  IconShieldExclamation,
  IconShoppingBag,
  IconShoppingCart,
  IconTool,
  IconTrash,
  IconTrophy,
  IconUser,
  IconWorld,
  IconX,
} from '@tabler/icons-react';
import PropTypes from 'prop-types';

/**
 * Centralized Icon Component
 * Wraps @tabler/icons-react with consistent styling and dynamic name resolution
 *
 * Usage:
 *   <Icon name="Dashboard" size={20} />
 *   <Icon name="User" stroke={2} color="#247bfe" />
 *
 * Features:
 * - Dynamic icon resolution from string names
 * - Consistent 2026+ styling defaults (stroke={1.5}, size={20})
 * - Fallback icon for missing names
 * - Development warnings for missing icons
 * - Support for both Ant-D aliases (Phase 1) and Tabler names (Phase 2)
 */

// Icon registry - maps string names to Tabler icon components
const ICON_REGISTRY = {
  // ✅ Tabler standard names (preferred - Phase 2)
  Dashboard: IconDashboard,
  World: IconWorld,
  ShoppingCart: IconShoppingCart,
  ShoppingBag: IconShoppingBag,
  Tool: IconTool,
  Database: IconDatabase,
  Settings: IconSettings,
  Headset: IconHeadset,
  CreditCard: IconCreditCard,
  Search: IconSearch,
  Login: IconLogin,
  Logout: IconLogout,
  User: IconUser,
  Message: IconMessage,
  ArrowLeft: IconArrowLeft,
  Trash: IconTrash,
  DotsVertical: IconDotsVertical,
  Eye: IconEye,
  Key: IconKey,
  Lock: IconLock,
  Shield: IconShield,
  ShieldCancel: IconShieldCancel,
  ShieldExclamation: IconShieldExclamation,
  // Dashboard KPI Cards
  CircleCheck: IconCircleCheck,
  CircleCheckFilled: IconCircleCheckFilled,
  Clock: IconClock,
  ShieldCheck: IconShieldCheck,
  AlertTriangle: IconAlertTriangle,
  AlertCircle: IconAlertCircle,
  // Additional dashboard icons
  Certificate: IconCertificate,
  Photo: IconPhoto,
  List: IconList,
  X: IconX,
  CircleX: IconCircleX,
  // Monitor tabs icons
  Calendar: IconCalendar,
  Link: IconLink,
  Hash: IconHash,
  MapPin: IconMapPin,
  Trophy: IconTrophy,
  // Domain Asset Manager icons
  Bulb: IconBulb,
  ChevronDown: IconChevronDown,
  ChevronLeft: IconChevronLeft,
  ChevronRight: IconChevronRight,
  Dots: IconDots,
  // Sidebar collapse/expand icons
  LayoutSidebarLeftCollapse: IconLayoutSidebarLeftCollapse,
  LayoutSidebarLeftExpand: IconLayoutSidebarLeftExpand,
  Plus: IconPlus,
  // Notifications page icons
  Bell: IconBell,
  Mail: IconMail,
  Refresh: IconRefresh,
  // Navigation icons
  Menu2: IconMenu2,
  // Order Details icons
  Building: IconBuilding,
  BuildingBank: IconBuildingBank,
  Briefcase: IconBriefcase,
  CalendarEvent: IconCalendarEvent,
  CurrencyDollar: IconCurrencyDollar,
  FileText: IconFileText,
  Phone: IconPhone,
  Receipt: IconReceipt,
  Server: IconServer,
  // Settings page icons
  AddressBook: IconAddressBook,
  // BrandyAI icons
  Rocket: IconRocket,

  // ⚠️ Ant-D compatibility aliases (Phase 1 - temporary)
  DashboardOutlined: IconDashboard,
  GlobalOutlined: IconWorld,
  ShoppingCartOutlined: IconShoppingCart,
  ShoppingOutlined: IconShoppingBag,
  ToolOutlined: IconTool,
  DatabaseOutlined: IconDatabase,
  SettingOutlined: IconSettings,
  CustomerServiceOutlined: IconHeadset,
  CreditCardOutlined: IconCreditCard,
  SearchOutlined: IconSearch,
  LoginOutlined: IconLogin,
  LogoutOutlined: IconLogout,
  UserOutlined: IconUser,
  MessageOutlined: IconMessage,
  LeftOutlined: IconArrowLeft,
  DeleteOutlined: IconTrash,
  // Dashboard KPI Cards (Ant-D aliases)
  CheckCircleOutlined: IconCircleCheck,
  CheckCircleFilled: IconCircleCheckFilled,
  CloseCircleOutlined: IconCircleX,
  ClockCircleOutlined: IconClock,
  ShieldOutlined: IconShieldCheck,
  WarningOutlined: IconAlertTriangle,
  InfoCircleOutlined: IconAlertCircle,
  // Additional dashboard icons (Ant-D aliases)
  SafetyCertificateOutlined: IconCertificate,
  PictureOutlined: IconPhoto,
  OrderedListOutlined: IconList,
  // Monitor tabs icons (Ant-D aliases)
  CalendarOutlined: IconCalendar,
  LinkOutlined: IconLink,
  NumberOutlined: IconHash,
  EnvironmentOutlined: IconMapPin,
  TrophyOutlined: IconTrophy,
  // Domain Asset Manager icons (Ant-D aliases)
  BulbOutlined: IconBulb,
  DownOutlined: IconChevronDown,
  RightOutlined: IconChevronRight,
  EllipsisOutlined: IconDots,
  // Notifications page icons (Ant-D aliases)
  BellOutlined: IconBell,
  MailOutlined: IconMail,
  SyncOutlined: IconRefresh,
};

// Fallback icon for missing names
const FallbackIcon = IconAlertCircle;

/**
 * Icon Component
 * @param {string} name - Icon name (e.g., "Dashboard", "DashboardOutlined")
 * @param {number} size - Icon size in pixels (default: 20)
 * @param {number} stroke - Stroke width (default: 1.5 for 2026+ elegant standards)
 * @param {string} color - Icon color (optional, inherits from parent if not set)
 * @param {string} className - Additional CSS classes
 * @param {object} style - Inline styles
 * @param {object} props - Additional props passed to Tabler icon
 */
const Icon = ({ name, size = 20, stroke = 1.5, color, className, style, ...props }) => {
  // Get icon component from registry
  const IconComponent = ICON_REGISTRY[name] || FallbackIcon;

  // Log warning for missing icons in development
  if (!ICON_REGISTRY[name] && process.env.NODE_ENV === 'development') {
    console.warn(
      `[Icon] Icon "${name}" not found in registry. Using fallback icon.`,
      '\nAvailable icons:',
      Object.keys(ICON_REGISTRY).join(', ')
    );
  }

  // Log deprecation warning for Ant-D aliases in development
  const isDeprecatedAlias = name && name.includes('Outlined');
  if (isDeprecatedAlias && process.env.NODE_ENV === 'development') {
    // Find the Tabler equivalent name
    const tablerName = Object.entries(ICON_REGISTRY).find(
      ([key, value]) => key === name && !key.includes('Outlined')
    );
    if (tablerName) {
      console.warn(
        `[Icon] "${name}" is a deprecated Ant-D alias.`,
        `Consider using "${tablerName[0]}" for Phase 2 migration.`
      );
    }
  }

  return (
    <IconComponent
      size={size}
      stroke={stroke}
      color={color}
      className={className}
      style={{ verticalAlign: 'middle', ...style }}
      {...props}
    />
  );
};

Icon.propTypes = {
  name: PropTypes.string.isRequired,
  size: PropTypes.number,
  stroke: PropTypes.number,
  color: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default Icon;
